import React from 'react';
import SEOHeading from '@/components/common/SEOHeading';
import Icon from '@/components/ui/AppIcon';

interface TrustFeature {
  icon: string;
  title: string;
  description: string;
}

const trustFeatures: TrustFeature[] = [
  {
    icon: "ShieldCheckIcon",
    title: "2-Year Health Guarantee",
    description: "Every single puppy comes with our comprehensive 2-year health guarantee. You're fully covered for genetic conditions, so you can relax and simply enjoy your new best friend."
  },
  {
    icon: "CurrencyDollarIcon",
    title: "30-Day Satisfaction Guarantee",
    description: "Take a full 30 days to make sure it's the perfect match for your family. If things don't work out for any reason, we'll refund your entire purchase price."
  },
  {
    icon: "AcademicCapIcon",
    title: "AKC Certified Breeders",
    description: "We're officially registered with the American Kennel Club and have been raising healthy, happy Labradors with love and expertise for over 15 years."
  },
  {
    icon: "HeartIcon",
    title: "Lifetime Support & Guidance",
    description: "Have questions about training, health concerns, or just need some advice? Call us anytime. We're here to support you and your puppy for their entire life."
  },
  {
    icon: "DocumentCheckIcon",
    title: "Complete Documentation Included",
    description: "Full health records, vaccination history, AKC registration papers, and microchip information all included. Complete transparency, no surprises."
  },
  {
    icon: "HomeModernIcon",
    title: "Home-Raised with Love",
    description: "Our puppies grow up right in our family home with children and other pets. They're beautifully socialized and ready to fit seamlessly into your life."
  }
];

const TrustIndicators = () => {
  return (
    <section className="bg-muted/30 py-16 lg:py-24">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="mb-12 text-center">
          <SEOHeading level={2} className="mb-4">
            Why Families Trust Luminex Labradors
          </SEOHeading>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            We're genuinely passionate about raising healthy, happy Labs and ensuring every family has an exceptional experience from start to finish.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {trustFeatures.map((feature, index) => (
            <div
              key={index}
              className="rounded-xl bg-card p-6 shadow-warm transition-smooth hover:shadow-warm-md"
            >
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-lg bg-primary/10">
                <Icon
                  name={feature.icon as any}
                  size={28}
                  variant="solid"
                  className="text-primary"
                />
              </div>
              <h3 className="mb-2 font-heading text-lg font-semibold text-foreground">
                {feature.title}
              </h3>
              <p className="text-muted-foreground">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Testimonial Highlights */}
        <div className="mt-16 grid gap-6 md:grid-cols-2">
          <div className="rounded-xl bg-card p-6 shadow-warm">
            <div className="mb-4 flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-lg font-bold text-primary">
                SM
              </div>
              <div>
                <p className="font-semibold text-foreground">Sarah Mitchell</p>
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Icon key={i} name="StarIcon" size={16} variant="solid" className="text-warning" />
                  ))}
                </div>
              </div>
            </div>
            <p className="text-muted-foreground italic">
              "Our puppy from Luminex Labradors is absolutely perfect! The entire team was so caring, knowledgeable, and helpful throughout the process. We couldn't be happier with our experience. Highly recommend!"
            </p>
          </div>
          
          <div className="rounded-xl bg-card p-6 shadow-warm">
            <div className="mb-4 flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-lg font-bold text-primary">
                JD
              </div>
              <div>
                <p className="font-semibold text-foreground">John Davis</p>
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Icon key={i} name="StarIcon" size={16} variant="solid" className="text-warning" />
                  ))}
                </div>
              </div>
            </div>
            <p className="text-muted-foreground italic">
              "Best decision we ever made! Our Lab is healthy, well-behaved, and has the sweetest personality. The comprehensive 2-year health guarantee gave us complete peace of mind."
            </p>
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-16 grid gap-8 rounded-2xl bg-gradient-to-br from-primary/5 to-secondary/5 p-8 sm:grid-cols-3 lg:p-12">
          <div className="text-center">
            <p className="mb-2 font-heading text-4xl font-bold text-primary lg:text-5xl">
              15+
            </p>
            <p className="text-muted-foreground">
              Years Raising Exceptional Labs
            </p>
          </div>
          <div className="text-center">
            <p className="mb-2 font-heading text-4xl font-bold text-primary lg:text-5xl">
              500+
            </p>
            <p className="text-muted-foreground">
              Happy Families We've Helped
            </p>
          </div>
          <div className="text-center">
            <p className="mb-2 font-heading text-4xl font-bold text-primary lg:text-5xl">
              4.9/5
            </p>
            <p className="text-muted-foreground">
              Average Customer Rating
            </p>
          </div>
        </div>

        {/* Trust Seals */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-8">
          <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-6 py-3 shadow-sm">
            <Icon name="ShieldCheckIcon" size={24} variant="solid" className="text-success" />
            <span className="font-semibold text-foreground">AKC Certified</span>
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-6 py-3 shadow-sm">
            <Icon name="CheckBadgeIcon" size={24} variant="solid" className="text-primary" />
            <span className="font-semibold text-foreground">BBB Accredited</span>
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-6 py-3 shadow-sm">
            <Icon name="HeartIcon" size={24} variant="solid" className="text-accent" />
            <span className="font-semibold text-foreground">Family Owned</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustIndicators;